import { useEffect, useState, useCallback } from "react";
import { Send, Trash2, MessageSquare, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const PAGE_SIZE = 10;

const formatDate = (iso) => {
    try {
        return new Date(iso).toLocaleString("pl-PL", {
            day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
    } catch { return ""; }
};

export default function Comments({ newsId, listingId, isAdmin }) {
    const parentType = listingId ? "listing" : "news";
    const parentId = listingId || newsId;
    const baseUrl = listingId ? `/listings/${listingId}/comments` : `/news/${newsId}/comments`;
    const deleteUrlBase = listingId ? "/listing-comments" : "/comments";

    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({ author_name: "", content: "", captcha_answer: "", website: "" });
    const [captcha, setCaptcha] = useState({ question: "", token: "" });
    const [loadTs, setLoadTs] = useState(Date.now());

    const fetchCaptcha = useCallback(() => {
        api.get("/captcha").then((r) => {
            setCaptcha({ question: r.data.question, token: r.data.token });
            setLoadTs(Date.now());
        }).catch(() => {});
    }, []);

    const fetchPage = useCallback(async (p) => {
        setLoading(true);
        try {
            const r = await api.get(baseUrl, { params: { page: p, page_size: PAGE_SIZE } });
            setItems(r.data.items || []);
            setPages(r.data.pages || 1);
            setTotal(r.data.total || 0);
            setPage(r.data.page || 1);
        } catch { /* ignore */ } finally { setLoading(false); }
    }, [baseUrl]);

    useEffect(() => { fetchPage(1); fetchCaptcha(); }, [fetchPage, fetchCaptcha]);

    const submit = async (e) => {
        e.preventDefault();
        if (Date.now() - loadTs < 3000) {
            toast.error("Zbyt szybko – chwila, sprawdzamy że to nie bot.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post(baseUrl, {
                author_name: form.author_name.trim(),
                content: form.content.trim(),
                captcha_token: captcha.token,
                captcha_answer: parseInt(form.captcha_answer, 10) || 0,
                website: form.website,
            });
            toast.success("Komentarz dodany. Dziękujemy!");
            setForm({ author_name: "", content: "", captcha_answer: "", website: "" });
            fetchCaptcha();
            await fetchPage(1);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
            fetchCaptcha();
            setForm((f) => ({ ...f, captcha_answer: "" }));
        } finally { setSubmitting(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Usunąć ten komentarz?")) return;
        try {
            await api.delete(`${deleteUrlBase}/${id}`);
            toast.success("Komentarz usunięty.");
            await fetchPage(page);
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    };

    return (
        <section className="bg-white rounded-3xl shadow-sm border border-gray-100 p-7 md:p-10" data-testid="comments-section">
            <header className="flex items-center justify-between gap-3 mb-6 flex-wrap">
                <h2 className="font-serif text-2xl md:text-3xl text-[#092E22] flex items-center gap-3">
                    <MessageSquare size={22} className="text-[#D4A537]" /> Komentarze
                    <span className="text-base text-gray-400 font-normal">({total})</span>
                </h2>
            </header>

            {/* Form */}
            <form onSubmit={submit} className="bg-[#FAF8F3] rounded-2xl p-5 md:p-6 mb-8" data-testid="comment-form">
                {/* honeypot */}
                <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                    tabIndex={-1}
                    autoComplete="off"
                    style={{ position: "absolute", left: "-9999px", width: 1, height: 1 }}
                    aria-hidden="true"
                />
                <div className="grid md:grid-cols-2 gap-3 mb-3">
                    <input
                        required
                        minLength={2}
                        maxLength={80}
                        placeholder="Imię i nazwisko"
                        value={form.author_name}
                        onChange={(e) => setForm({ ...form, author_name: e.target.value })}
                        data-testid="comment-author"
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-[#0D4F3C]"
                    />
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 whitespace-nowrap">{captcha.question || "…"}</span>
                        <input
                            required
                            type="number"
                            placeholder="="
                            value={form.captcha_answer}
                            onChange={(e) => setForm({ ...form, captcha_answer: e.target.value })}
                            data-testid="comment-captcha-answer"
                            className="w-20 px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-center focus:outline-none focus:border-[#0D4F3C]"
                        />
                        <button type="button" onClick={fetchCaptcha} className="text-gray-400 hover:text-[#0D4F3C]" title="Wygeneruj nowe pytanie" data-testid="comment-captcha-refresh">
                            <RefreshCw size={14} />
                        </button>
                    </div>
                </div>
                <textarea
                    required
                    minLength={2}
                    maxLength={2000}
                    rows={3}
                    placeholder="Twój komentarz..."
                    value={form.content}
                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                    data-testid="comment-content"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:border-[#0D4F3C] resize-y"
                />
                <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
                    <p className="text-[11px] text-gray-500">
                        Zostawiając komentarz akceptujesz politykę prywatności. Komentarze są moderowane – obraźliwe lub spamerskie zostaną usunięte.
                    </p>
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="comment-submit"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white text-sm font-medium disabled:opacity-60"
                    >
                        <Send size={14} /> {submitting ? "Wysyłanie..." : "Dodaj komentarz"}
                    </button>
                </div>
            </form>

            {/* List */}
            {loading && <p className="text-gray-500 text-sm">Wczytywanie komentarzy...</p>}
            {!loading && items.length === 0 && (
                <p className="text-gray-500 text-sm" data-testid="comments-empty">Bądź pierwszą osobą, która zostawi komentarz pod tą wiadomością.</p>
            )}
            <div className="space-y-4">
                {items.map((c) => (
                    <article key={c.id} data-testid={`comment-${c.id}`} className="border-l-2 border-[#D4A537]/30 pl-5 py-2">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <p className="font-semibold text-[#092E22] text-sm">{c.author_name}</p>
                                <p className="text-[11px] text-gray-400">{formatDate(c.created_at)}</p>
                            </div>
                            {isAdmin && (
                                <button onClick={() => remove(c.id)} data-testid={`comment-delete-${c.id}`} className="text-red-500 hover:text-red-700 p-1 rounded-lg hover:bg-red-50" title="Usuń komentarz">
                                    <Trash2 size={14} />
                                </button>
                            )}
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-line mt-2 break-words">{c.content}</p>
                    </article>
                ))}
            </div>

            {/* Pagination */}
            {pages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-7" data-testid="comments-pagination">
                    <button
                        onClick={() => fetchPage(Math.max(1, page - 1))}
                        disabled={page <= 1 || loading}
                        data-testid="comments-prev"
                        className="px-4 py-2 rounded-full border border-gray-200 text-sm disabled:opacity-40 hover:bg-[#FAF8F3]"
                    >
                        ‹ Poprzednia
                    </button>
                    <span className="text-sm text-gray-600">Strona {page} z {pages}</span>
                    <button
                        onClick={() => fetchPage(Math.min(pages, page + 1))}
                        disabled={page >= pages || loading}
                        data-testid="comments-next"
                        className="px-4 py-2 rounded-full border border-gray-200 text-sm disabled:opacity-40 hover:bg-[#FAF8F3]"
                    >
                        Następna ›
                    </button>
                </div>
            )}
        </section>
    );
}
